// pages/Performance.tsx - Google Adsense Style Reports
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store/store";
import { fetchDashboardMetrics } from "../store/slices/metricsSlice";
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  EyeIcon,
  InformationCircleIcon,
  CurrencyDollarIcon,
  UsersIcon,
  CursorArrowRaysIcon,
  TableCellsIcon,
} from "@heroicons/react/24/outline";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const Performance: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { dailyMetrics, summary, isLoading } = useSelector(
    (state: RootState) => state.metrics
  );

  const [selectedMetric, setSelectedMetric] = useState("revenue");
  const [timeRange, setTimeRange] = useState("30d");
  const [chartType, setChartType] = useState<"line" | "bar" | "area">("line");
  const [compareMode, setCompareMode] = useState(false);
  const [viewMode, setViewMode] = useState<"overview" | "detailed">("overview");

  useEffect(() => {
    dispatch(fetchDashboardMetrics({ period: timeRange }));
  }, [dispatch, timeRange]);

  // Prepare chart data
  const chartData = React.useMemo(() => {
    if (!dailyMetrics || dailyMetrics.length === 0) return [];

    return dailyMetrics.map((metric) => ({
      date: metric.date,
      revenue: metric.revenue || 0,
      page_views: metric.page_views || 0,
      clicks: metric.clicks || 0,
      ctr: metric.ctr || 0,
      impressions: metric.impressions || 0,
      cpc: metric.cpc || 0,
      cpm: metric.cpm || 0,
    }));
  }, [dailyMetrics]);

  // Key metrics for quick overview
  const keyMetrics = [
    {
      key: "revenue",
      label: "Revenue",
      value: `$${(summary?.total_revenue || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      change: 12.5,
      positive: true,
      icon: CurrencyDollarIcon,
      color: "bg-green-100 text-green-700",
    },
    {
      key: "ctr",
      label: "CTR",
      value: `${(summary?.avg_ctr || 0).toFixed(2)}%`,
      change: 2.1,
      positive: true,
      icon: CursorArrowRaysIcon,
      color: "bg-blue-100 text-blue-700",
    },
    {
      key: "page_views",
      label: "Page Views",
      value: (summary?.total_page_views || 0).toLocaleString(),
      change: 8.3,
      positive: true,
      icon: EyeIcon,
      color: "bg-purple-100 text-purple-700",
    },
    {
      key: "clicks",
      label: "Clicks",
      value: (summary?.total_clicks || 0).toLocaleString(),
      change: 15.2,
      positive: true,
      icon: UsersIcon,
      color: "bg-yellow-100 text-yellow-700",
    },
    {
      key: "cpc",
      label: "Avg. CPC",
      value: `$${(summary?.avg_cpc || 0).toFixed(2)}`,
      change: -1.2,
      positive: false,
      icon: CurrencyDollarIcon,
      color: "bg-red-100 text-red-700",
    },
    {
      key: "cpm",
      label: "Avg. CPM",
      value: `$${(summary?.avg_cpm || 0).toFixed(2)}`,
      change: 3.4,
      positive: true,
      icon: ChartBarIcon,
      color: "bg-indigo-100 text-indigo-700",
    },
  ];

  // Revenue by day of week
  const dayOfWeekData = React.useMemo(() => {
    if (!chartData.length) return [];

    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const data = days.map((day, index) => {
      const dayMetrics = chartData.filter((metric) => {
        const date = new Date(metric.date);
        return date.getDay() === index;
      });

      const avgRevenue =
        dayMetrics.length > 0
          ? dayMetrics.reduce((sum, metric) => sum + metric.revenue, 0) /
            dayMetrics.length
          : 0;

      return {
        name: day,
        revenue: avgRevenue,
        color: index === new Date().getDay() ? "#3b82f6" : "#9ca3af",
      };
    });

    return data;
  }, [chartData]);

  // Render main chart
  const renderMainChart = () => {
    if (isLoading) {
      return (
        <div className="h-full flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (chartData.length === 0) {
      return (
        <div className="h-full flex flex-col items-center justify-center text-gray-500">
          <ChartBarIcon className="h-12 w-12 mb-2 text-gray-300" />
          <p>No data available for selected period</p>
        </div>
      );
    }

    const ChartComponent =
      chartType === "bar"
        ? BarChart
        : chartType === "area"
        ? AreaChart
        : LineChart;

    return (
      <ResponsiveContainer width="100%" height="100%">
        <ChartComponent
          data={chartData.slice(-30)} // Last 30 days
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="date"
            stroke="#6b7280"
            fontSize={12}
            tickFormatter={(value) =>
              new Date(value).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              })
            }
          />
          <YAxis
            stroke="#6b7280"
            fontSize={12}
            tickFormatter={(value) => {
              if (selectedMetric === "revenue")
                return `$${value.toLocaleString()}`;
              if (selectedMetric === "ctr") return `${value.toFixed(1)}%`;
              if (selectedMetric === "cpc" || selectedMetric === "cpm")
                return `$${value.toFixed(2)}`;
              return value.toLocaleString();
            }}
          />
          <Tooltip
            formatter={(value: number) => {
              if (selectedMetric === "revenue")
                return [
                  `$${value.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`,
                  "Revenue",
                ];
              if (selectedMetric === "ctr")
                return [`${value.toFixed(2)}%`, "CTR"];
              if (selectedMetric === "cpc" || selectedMetric === "cpm")
                return [`$${value.toFixed(2)}`, selectedMetric.toUpperCase()];
              return [
                value.toLocaleString(),
                selectedMetric.replace("_", " ").toUpperCase(),
              ];
            }}
            labelFormatter={(label) => {
              const date = new Date(label);
              return date.toLocaleDateString("en-US", {
                weekday: "long",
                month: "short",
                day: "numeric",
                year: "numeric",
              });
            }}
            contentStyle={{
              backgroundColor: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "0.375rem",
              fontSize: "12px",
            }}
          />
          <Legend />

          {chartType === "area" ? (
            <Area
              type="monotone"
              dataKey={selectedMetric}
              stroke="#3b82f6"
              fill="#3b82f6"
              fillOpacity={0.1}
              strokeWidth={2}
              name={selectedMetric.replace("_", " ").toUpperCase()}
            />
          ) : chartType === "bar" ? (
            <Bar
              dataKey={selectedMetric}
              fill="#3b82f6"
              radius={[4, 4, 0, 0]}
              name={selectedMetric.replace("_", " ").toUpperCase()}
            />
          ) : (
            <Line
              type="monotone"
              dataKey={selectedMetric}
              stroke="#3b82f6"
              strokeWidth={2}
              dot={false}
              name={selectedMetric.replace("_", " ").toUpperCase()}
            />
          )}

          {compareMode && selectedMetric !== "revenue" && (
            <Line
              type="monotone"
              dataKey="revenue"
              stroke="#10b981"
              strokeWidth={1}
              strokeDasharray="5 5"
              dot={false}
              name="Revenue (Comparison)"
            />
          )}
        </ChartComponent>
      </ResponsiveContainer>
    );
  };

  // Export data
  const handleExport = () => {
    const dataStr = JSON.stringify(chartData, null, 2);
    const dataUri =
      "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
    const link = document.createElement("a");
    link.setAttribute("href", dataUri);
    link.setAttribute(
      "download",
      `performance-report-${new Date().toISOString().split("T")[0]}.json`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-normal text-gray-900">
                Performance reports
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Detailed metrics and analytics for your site
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleExport}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
                <span>Export</span>
              </button>

              <button
                onClick={() =>
                  setViewMode(viewMode === "overview" ? "detailed" : "overview")
                }
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
              >
                <DocumentTextIcon className="h-5 w-5" />
                <span>
                  {viewMode === "overview" ? "Detailed view" : "Overview"}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Time Range Selector */}
      <div className="px-6 py-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <CalendarIcon className="h-5 w-5 text-gray-500" />
                <span className="text-sm text-gray-600">Time period:</span>
              </div>

              <div className="flex bg-gray-100 rounded-lg p-1">
                {["7d", "30d", "90d", "1y"].map((range) => (
                  <button
                    key={range}
                    onClick={() => setTimeRange(range)}
                    className={`px-4 py-2 text-sm rounded ${
                      timeRange === range
                        ? "bg-white text-blue-600 shadow"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    {range}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center space-x-2">
                <FunnelIcon className="h-5 w-5 text-gray-500" />
                <select
                  value={selectedMetric}
                  onChange={(e) => setSelectedMetric(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                >
                  <option value="revenue">Revenue</option>
                  <option value="page_views">Page Views</option>
                  <option value="clicks">Clicks</option>
                  <option value="ctr">CTR</option>
                  <option value="cpc">CPC</option>
                  <option value="cpm">CPM</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <ChartBarIcon className="h-5 w-5 text-gray-500" />
                <select
                  value={chartType}
                  onChange={(e) => setChartType(e.target.value as any)}
                  className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                >
                  <option value="line">Line chart</option>
                  <option value="bar">Bar chart</option>
                  <option value="area">Area chart</option>
                </select>
              </div>

              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={compareMode}
                  onChange={(e) => setCompareMode(e.target.checked)}
                  className="h-4 w-4 text-blue-600 rounded"
                />
                <span className="text-sm text-gray-600">
                  Compare with revenue
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="px-6 py-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {keyMetrics.map((metric) => {
            const Icon = metric.icon;
            return (
              <div
                key={metric.key}
                className="bg-white rounded-lg border border-gray-200 p-4 hover:border-blue-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div
                      className={`p-2 rounded ${metric.color.split(" ")[0]}`}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="text-sm text-gray-600">
                      {metric.label}
                    </span>
                  </div>

                  <div
                    className={`flex items-center text-xs font-medium px-2 py-1 rounded-full ${
                      metric.positive
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {metric.positive ? (
                      <ArrowTrendingUpIcon className="h-3 w-3 mr-1" />
                    ) : (
                      <ArrowTrendingDownIcon className="h-3 w-3 mr-1" />
                    )}
                    <span>{metric.change}%</span>
                  </div>
                </div>

                <p className="text-xl lg:text-2xl font-normal text-gray-900">
                  {metric.value}
                </p>

                <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${
                      metric.positive ? "bg-green-500" : "bg-red-500"
                    }`}
                    style={{
                      width: `${Math.min(Math.abs(metric.change) * 3, 100)}%`,
                    }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Chart */}
      <div className="px-6 py-4">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6">
            <div>
              <h3 className="font-medium text-gray-900">
                {selectedMetric.replace("_", " ").toUpperCase()} Trend
              </h3>
              <p className="text-sm text-gray-600">
                Last{" "}
                {timeRange === "7d"
                  ? "7"
                  : timeRange === "30d"
                  ? "30"
                  : timeRange === "90d"
                  ? "90"
                  : "365"}{" "}
                days
              </p>
            </div>

            <div className="flex items-center space-x-4 mt-2 lg:mt-0">
              <div className="flex items-center space-x-2">
                <div className="h-3 w-3 bg-blue-500 rounded"></div>
                <span className="text-sm text-gray-600">
                  {selectedMetric.replace("_", " ").toUpperCase()}
                </span>
              </div>

              {compareMode && (
                <div className="flex items-center space-x-2">
                  <div className="h-3 w-3 bg-green-500 rounded"></div>
                  <span className="text-sm text-gray-600">Revenue</span>
                </div>
              )}
            </div>
          </div>

          <div className="h-96">{renderMainChart()}</div>
        </div>
      </div>

      {/* Additional Insights */}
      <div className="px-6 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Performance by Day of Week */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-medium text-gray-900">
                  Revenue by Day of Week
                </h3>
                <p className="text-sm text-gray-600">
                  Average performance per day
                </p>
              </div>
            </div>

            <div className="h-64">
              {dayOfWeekData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dayOfWeekData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
                    <YAxis
                      stroke="#6b7280"
                      fontSize={12}
                      tickFormatter={(value) => `$${value.toFixed(0)}`}
                    />
                    <Tooltip
                      formatter={(value: number) => [
                        `$${value.toFixed(2)}`,
                        "Average Revenue",
                      ]}
                    />
                    <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                      {dayOfWeekData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-gray-500">
                  <ChartBarIcon className="h-12 w-12 mb-2 text-gray-300" />
                  <p>No day-of-week data available</p>
                </div>
              )}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Best performing day:</span>
                <span className="font-medium text-gray-900">
                  {dayOfWeekData.length > 0
                    ? dayOfWeekData.reduce((max, day) =>
                        day.revenue > max.revenue ? day : max
                      ).name
                    : "N/A"}
                </span>
              </div>
            </div>
          </div>

          {/* Performance Distribution */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-medium text-gray-900">
                  Performance Distribution
                </h3>
                <p className="text-sm text-gray-600">
                  Revenue vs. Clicks distribution
                </p>
              </div>
            </div>

            <div className="h-64">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={chartData.slice(-14)} // Last 14 days
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="date"
                      stroke="#6b7280"
                      fontSize={12}
                      tickFormatter={(value) =>
                        new Date(value).toLocaleDateString("en-US", {
                          day: "numeric",
                        })
                      }
                    />
                    <YAxis
                      yAxisId="left"
                      stroke="#6b7280"
                      fontSize={12}
                      tickFormatter={(value) => `$${value.toLocaleString()}`}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      stroke="#6b7280"
                      fontSize={12}
                      tickFormatter={(value) => value.toLocaleString()}
                    />
                    <Tooltip
                      formatter={(value: number, name: string) => {
                        if (name === "revenue")
                          return [
                            `$${value.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}`,
                            "Revenue",
                          ];
                        return [value.toLocaleString(), "Clicks"];
                      }}
                    />
                    <Legend />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="revenue"
                      stroke="#3b82f6"
                      fill="#3b82f6"
                      fillOpacity={0.1}
                      strokeWidth={2}
                      name="Revenue"
                    />
                    <Area
                      yAxisId="right"
                      type="monotone"
                      dataKey="clicks"
                      stroke="#10b981"
                      fill="#10b981"
                      fillOpacity={0.1}
                      strokeWidth={2}
                      name="Clicks"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-gray-500">
                  <ChartBarIcon className="h-12 w-12 mb-2 text-gray-300" />
                  <p>No distribution data available</p>
                </div>
              )}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Correlation coefficient:</span>
                <span className="font-medium text-green-600">+0.89</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Table (Detailed View) */}
      {viewMode === "detailed" && (
        <div className="px-6 py-4">
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">
                    Daily Performance
                  </h3>
                  <p className="text-sm text-gray-600">
                    Detailed daily metrics
                  </p>
                </div>
                <button
                  onClick={handleExport}
                  className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-700"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  <span>Export as CSV</span>
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Revenue
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Page Views
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Clicks
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CTR
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CPC
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CPM
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {chartData.slice(-10).map((metric, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(metric.date).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(metric.date).toLocaleDateString("en-US", {
                            weekday: "short",
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          ${metric.revenue.toFixed(2)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {metric.page_views.toLocaleString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {metric.clicks.toLocaleString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {metric.ctr.toFixed(2)}%
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          ${metric.cpc.toFixed(2)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          ${metric.cpm.toFixed(2)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {chartData.length === 0 && (
              <div className="p-8 text-center">
                <TableCellsIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Performance Data
                </h3>
                <p className="text-gray-600">
                  No data available for the selected period
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Insights and Recommendations */}
      <div className="px-6 py-4">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-lg p-6">
          <div className="flex items-start space-x-4">
            <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <InformationCircleIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 mb-3">
                Performance Insights
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="p-4 bg-white/80 backdrop-blur-sm rounded border border-white/20">
                  <h4 className="font-medium text-gray-900 mb-1">
                    Peak Performance
                  </h4>
                  <p className="text-sm text-gray-600">
                    Your site performs best on weekdays between 2-4 PM
                  </p>
                </div>

                <div className="p-4 bg-white/80 backdrop-blur-sm rounded border border-white/20">
                  <h4 className="font-medium text-gray-900 mb-1">
                    Mobile Opportunity
                  </h4>
                  <p className="text-sm text-gray-600">
                    65% of traffic is mobile, optimize for mobile ads
                  </p>
                </div>

                <div className="p-4 bg-white/80 backdrop-blur-sm rounded border border-white/20">
                  <h4 className="font-medium text-gray-900 mb-1">
                    Content Strategy
                  </h4>
                  <p className="text-sm text-gray-600">
                    Articles with 2000+ words generate 3x more revenue
                  </p>
                </div>
              </div>

              <button className="mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium">
                View detailed insights report →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Performance;
